import { UserRole } from '@heidi/schema/ml-scribe';
import { ENTITLEMENT } from '@heidi/workspace/contracts';
import {
  useCombinedQueriesTeamGeneralInfo,
  useCurrentWorkspace,
  useQueryEntitlements,
  useQueryJoinedTeams,
  useQueryTeamDetails,
  useQueryUser,
  useQueryWorkspaces,
} from '@heidi/workspace/data';

import { HEIDI_2_AWARENESS_BANNER_MODAL_ID } from '../../../contracts/heidi-2';
import { getHeidi2Ports, resolveHeidi2WelcomeOutcome } from '../../../data/heidi-2';

import { computeShouldShowHeidi2ActivatedWelcome } from './heidi-2-activated-welcome-eligibility';
import {
  computeHasFullTeamEvidenceAccess,
  computeShouldShowHeidi2OptIn,
  isClinicianTrialModalBlockingHeidi2OptIn,
} from './heidi-2-opt-in-eligibility';

/**
 * When this page load started. `getUserBillingInfo` is in
 * `PERSISTED_QUERY_KEYS`, so it rehydrates from localStorage as `success`
 * carrying the PREVIOUS session's payload and only then refetches — a window
 * in which a Heidi 2 account still reads as v1.
 */
const APP_STARTED_AT = Date.now();

/**
 * `activate`: an admin still on Heidi 1, whose tour switches the workspace over.
 * `activated`: anyone on a team already on Heidi II, whose tour must not.
 */
export type Heidi2WelcomeCohort = 'activate' | 'activated';

export interface Heidi2WelcomeCohortDecision {
  cohort: Heidi2WelcomeCohort | null;
  /** `cohort: null` means both "not due" and "still loading"; this tells them apart. */
  isDecided: boolean;
  /**
   * Cohort for Scribe ranking, ignoring recording and a queued trial modal.
   * `undefined` while a deciding input loads or retries; terminal failure is `null`.
   */
  priorityCohort: Heidi2WelcomeCohort | null | undefined;
}

/** Which Heidi II welcome this viewer is due, or `null` for none. */
export const useHeidi2WelcomeCohort = (): Heidi2WelcomeCohort | null =>
  useHeidi2WelcomeCohortDecision().cohort;

export const useHeidi2WelcomeCohortDecision = (): Heidi2WelcomeCohortDecision => {
  const ports = getHeidi2Ports();
  const isHeidi2OptInEnabled = ports.useIsHeidi2OptInEnabled();
  const isHeidi2OnboardingEnabled = ports.useIsHeidi2OnboardingEnabled();
  const isWelcomeSuppressed = ports.useIsHeidi2WelcomeSuppressed();
  const isWorkspaceIdSettled = ports.useWorkspaceIdSettledForCreditUsagePopoverLaunch();
  const {
    data: billingInfo,
    isPending: isBillingInfoPending,
    isError: isBillingInfoError,
    isFetching: isBillingInfoFetching,
    dataUpdatedAt: billingInfoUpdatedAt,
  } = ports.useUserBillingInfo();
  // Restored-from-storage billing reads as settled, so the cohort gate below
  // would run against last session's `heidi_version` — mounting the tour for
  // an account already on Heidi 2 and firing `activate-heidi-2` off a stale
  // answer. Nothing shows until a response from THIS load has landed.
  const hasFreshBillingInfo = billingInfoUpdatedAt >= APP_STARTED_AT;
  const modalAppState = ports.useModalLaunchState();
  const seenModalIds = ports.useSeenModalIds();
  // Same inputs that unblock `launchModalsAtom`. Until they settle,
  // CLINICIAN_TRIAL is not in the queue yet and this surface would win the race.
  const isClinicianTrialEligibilityLoaded =
    modalAppState.isUserDataLoaded &&
    !modalAppState.isUserPending &&
    !modalAppState.isKindePropertiesPending &&
    seenModalIds !== null &&
    !isBillingInfoPending &&
    hasFreshBillingInfo;
  // Rematch containment: timer not read; session status + Chronicle still suppress.
  const isClinicalRecordingActive = ports.useIsClinicalRecordingActive(false);

  const joinedTeamsQuery = useQueryJoinedTeams();
  const currentWorkspace = useCurrentWorkspace();
  const workspacesQuery = useQueryWorkspaces();
  const isWorkspaceAdmin = currentWorkspace?.user_role === UserRole.ADMIN;
  const { hasTeam, isTeamDataReady, teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const team = teamDetails?.team;
  const teamDetailsQuery = useQueryTeamDetails(
    joinedTeamsQuery.data?.joined_teams?.at(0)?.team_id
  );
  const { data: user } = useQueryUser((user) =>
    user
      ? {
          isManaged: user.is_managed,
          welcomeView: user.seen_modals?.[HEIDI_2_AWARENESS_BANNER_MODAL_ID],
        }
      : undefined
  );
  const isManaged = user?.isManaged;
  // Admin-only endpoints. Skip them unless this viewer can see the cohort:
  // opt-in on, and not managed (unknown counts as managed).
  const shouldLoadTeamEvidence = isHeidi2OptInEnabled && isManaged === false;
  const teamId =
    shouldLoadTeamEvidence && isWorkspaceAdmin && team?.id ? team.id : undefined;
  const teamEntitlementsQuery = ports.useQueryTeamAccessEntitlements(teamId);
  const roleIdsCanAccessEvidence =
    team?.team_evidence_settings?.role_ids_can_access_evidence;
  // Empty allow-list is unrestricted, so the role catalog is only needed when
  // the list might be "every role" rather than a subset.
  const needsRoleCatalog = (roleIdsCanAccessEvidence ?? []).some(
    (id) => typeof id === 'string' && id.length > 0
  );
  const rolesQuery = ports.useQueryRoles(needsRoleCatalog ? teamId : undefined);

  // The viewer's OWN merged (user + team + org) revoke, which is what team
  // settings writes — `heidi_version` is believed to follow it, but only this
  // row is ours to read. Unsettled or errored stays `undefined` and suppresses.
  const revokeQuery = useQueryEntitlements([ENTITLEMENT.HEIDI_II_ACCESS_REVOKED]);
  const isHeidi2Revoked = revokeQuery.isSuccess
    ? (revokeQuery.data?.get(ENTITLEMENT.HEIDI_II_ACCESS_REVOKED)?.enabled ?? false)
    : undefined;

  const currentQueuedModalId = ports.useCurrentQueuedModalId();
  const queueHeadModalId = ports.useQueueHeadModalId();
  const teamEntitlements = !isTeamDataReady
    ? undefined
    : !hasTeam
      ? []
      : teamEntitlementsQuery.isError ||
          teamEntitlementsQuery.isPending ||
          teamEntitlementsQuery.data === undefined
        ? undefined
        : (teamEntitlementsQuery.data.team_entitlements ?? []);
  const teamRoleIds =
    !needsRoleCatalog ||
    rolesQuery.isError ||
    rolesQuery.isPending ||
    rolesQuery.data?.data == null
      ? undefined
      : rolesQuery.data.data.map((role) => role.id);
  const hasFullTeamEvidenceAccess = computeHasFullTeamEvidenceAccess({
    isTeamMembershipKnown: isTeamDataReady,
    hasTeam,
    teamEntitlements,
    accessMode: team?.team_evidence_settings?.access_mode,
    roleIdsCanAccessEvidence,
    teamRoleIds,
  });
  const isClinicianTrialModalBlocking = isClinicianTrialModalBlockingHeidi2OptIn({
    currentQueuedModalId,
    queueHeadModalId,
  });
  // Hooks above all run unconditionally; the suppression only gates the answer.
  if (isWelcomeSuppressed) {
    return { cohort: null, isDecided: true, priorityCohort: null };
  }
  const activationGate = {
    isHeidi2OptInEnabled,
    isWorkspaceIdSettled,
    isClinicalRecordingActive,
    isUserManaged: isManaged,
    isWorkspaceAdmin,
    hasFullTeamEvidenceAccess,
    isClinicianTrialModalBlocking,
    isClinicianTrialEligibilityLoaded,
    isMaxBillingSeat: ports.isMaxBillingSeat,
  };
  const activatedGate = {
    isHeidi2OnboardingEnabled,
    isClinicianTrialEligibilityLoaded,
    isWorkspaceIdSettled,
    isClinicalRecordingActive,
    isClinicianTrialModalBlocking,
    isUserManaged: isManaged,
    hasTeam,
    heidiVersion: billingInfo?.heidi_version,
    isHeidi2Revoked,
    welcomeOutcome: resolveHeidi2WelcomeOutcome(user?.welcomeView),
  };
  const cohort = computeShouldShowHeidi2OptIn(activationGate, billingInfo)
    ? 'activate'
    : computeShouldShowHeidi2ActivatedWelcome(activatedGate)
      ? 'activated'
      : null;

  // Reuse the cohort predicates, but never mistake a temporary clinical/queue
  // blocker for a settled audience exclusion. The route host handles boot routes.
  const temporaryBlockersDisabled = {
    isClinicalRecordingActive: false,
    isClinicianTrialModalBlocking: false,
  };
  // Ask the same predicates whether workspace/team inputs could matter. A
  // billing/flag-excluded viewer need not wait on disabled admin queries.
  const couldActivate = computeShouldShowHeidi2OptIn(
    {
      ...activationGate,
      ...temporaryBlockersDisabled,
      isWorkspaceAdmin: true,
      hasFullTeamEvidenceAccess: true,
    },
    billingInfo
  );
  const couldJoinActivatedTeam = computeShouldShowHeidi2ActivatedWelcome({
    ...activatedGate,
    ...temporaryBlockersDisabled,
    hasTeam: true,
    isHeidi2Revoked: false,
  });
  const needsTeamData = couldActivate || couldJoinActivatedTeam;
  const needsAdminEvidence = couldActivate && isWorkspaceAdmin && hasTeam;
  // Team details swallow transport errors, so a settled lookup can end with no
  // team. Only activation reads the team, so that ends the admin path alone.
  const isTeamDetailsErrorFinal =
    teamDetailsQuery.isError && !teamDetailsQuery.isFetching;
  const hasSettledWithoutTeam =
    hasTeam && team == null && (teamDetailsQuery.isSuccess || isTeamDetailsErrorFinal);
  // A successful empty list has no workspace to activate. An unmatched id is
  // still hydrating, awaiting the selector or reloading, so it keeps waiting.
  const hasNoWorkspace =
    workspacesQuery.isSuccess && workspacesQuery.data?.workspaces.length === 0;
  // An exhausted query cannot make Heidi eligible. A retry/refetch still can,
  // so retain priority until that fetch settles rather than flashing Scribe.
  const hasTerminalError =
    (isBillingInfoError && !isBillingInfoFetching) ||
    (couldActivate && workspacesQuery.isError && !workspacesQuery.isFetching) ||
    (needsTeamData && joinedTeamsQuery.isError && !joinedTeamsQuery.isFetching) ||
    (couldActivate && hasTeam && (isTeamDetailsErrorFinal || hasSettledWithoutTeam)) ||
    (needsAdminEvidence &&
      ((teamEntitlementsQuery.isError && !teamEntitlementsQuery.isFetching) ||
        (needsRoleCatalog && rolesQuery.isError && !rolesQuery.isFetching)));
  const isDecided =
    isClinicianTrialEligibilityLoaded &&
    isWorkspaceIdSettled &&
    isTeamDataReady &&
    isManaged !== undefined &&
    !revokeQuery.isPending;
  // Scribe arbitration also needs this load's answers for the inputs that can
  // still make the viewer due; the boot hold's decision alone accepts a
  // restored team list or an unloaded admin read.
  const isPriorityReady =
    isClinicianTrialEligibilityLoaded &&
    isWorkspaceIdSettled &&
    isTeamDataReady &&
    isManaged !== undefined &&
    (!couldJoinActivatedTeam || !revokeQuery.isPending) &&
    !!billingInfo &&
    (!couldActivate || currentWorkspace !== null || hasNoWorkspace) &&
    (!needsTeamData ||
      (joinedTeamsQuery.isSuccess &&
        joinedTeamsQuery.dataUpdatedAt >= APP_STARTED_AT &&
        (!hasTeam || team != null || hasSettledWithoutTeam))) &&
    (!needsAdminEvidence ||
      (teamEntitlements !== undefined &&
        (!needsRoleCatalog || teamRoleIds !== undefined)));
  const resolvePriorityCohort = (): Heidi2WelcomeCohort | null | undefined => {
    if (!isHeidi2OptInEnabled && !isHeidi2OnboardingEnabled) return null;
    if (hasTerminalError) return null;
    if (!isPriorityReady) return undefined;
    if (
      computeShouldShowHeidi2OptIn(
        { ...activationGate, ...temporaryBlockersDisabled },
        billingInfo
      )
    ) {
      return 'activate';
    }
    return computeShouldShowHeidi2ActivatedWelcome({
      ...activatedGate,
      ...temporaryBlockersDisabled,
    })
      ? 'activated'
      : null;
  };
  return { cohort, isDecided, priorityCohort: resolvePriorityCohort() };
};
